import { emptySettings } from "../landingEditor/config";
import {
  DEFAULT_SEGMENT_VISUAL_STYLE,
  SEGMENT_LABELS,
} from "./segmentVisualStyle";

export const SEGMENT_FIELD_TYPES = Object.freeze({
  TEXT: "text",
  TEXTAREA: "textarea",
  BOOLEAN: "boolean",
  URL: "url",
  EMAIL: "email",
  COLOR: "color",
  IMAGE: "image",
  SELECT: "select",
  NUMBER: "number",
  COLLECTION: "collection",
  SEGMENT_VISUAL_STYLE: "segment-visual-style",
});

export const SEGMENT_TYPES = Object.freeze({
  NAVBAR: "navbar",
  HERO: "hero",
  SPOTLIGHT: "spotlight",
  FEATURED: "featured",
  BRANDS: "brands",
  CATEGORIES: "categories",
  ABOUT: "about",
  HIGHLIGHTS: "highlights",
  CONTACT: "contact",
  FOOTER: "footer",
  FLOATING_WHATSAPP: "floatingWhatsapp",
});

const URL_MAX_LENGTH = 2000;
const VISUAL_LEVEL_LIMITS = Object.freeze({ min: 1, max: 3, step: 1 });
const IMAGE_POSITION_OPTIONS = Object.freeze([
  { value: "center", label: "Centro" },
  { value: "top", label: "Arriba" },
  { value: "bottom", label: "Abajo" },
  { value: "left", label: "Izquierda" },
  { value: "right", label: "Derecha" },
]);
const MEGA_MENU_STYLE_OPTIONS = Object.freeze([
  { value: "auto", label: "Automático según el contenido" },
  { value: "elegant", label: "Elegante · servicios" },
  { value: "catalog", label: "Catálogo · productos" },
  { value: "showcase", label: "Destacados · hasta 3 ítems" },
]);
const MEGA_MENU_CONTENT_OPTIONS = Object.freeze([
  { value: "auto", label: "Automático según el catálogo" },
  { value: "products", label: "Sólo productos" },
  { value: "services", label: "Sólo servicios" },
  { value: "mixed", label: "Productos y servicios" },
]);
const MEGA_MENU_DISPLAY_OPTIONS = Object.freeze([
  { value: "categories", label: "Sólo categorías" },
  { value: "items", label: "Sólo ítems" },
  { value: "both", label: "Categorías + ítems" },
]);
const MEGA_MENU_HIERARCHY_OPTIONS = Object.freeze([
  { value: "category-first", label: "Categoría → subcategoría" },
  { value: "subcategory-first", label: "Subcategoría → categoría" },
]);
const STYLE_OPTIONS = Object.freeze([
  { value: "style-1", label: "Estilo 1" },
  { value: "style-2", label: "Estilo 2" },
  { value: "style-3", label: "Estilo 3" },
]);
const HERO_CTA_ICON_OPTIONS = Object.freeze([
  { value: "explore", label: "Explorar / brújula" },
  { value: "storefront", label: "Tienda / local" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "phone", label: "Teléfono" },
  { value: "mail", label: "Email" },
  { value: "place", label: "Ubicación" },
  { value: "shopping", label: "Bolsa de compra" },
  { value: "event", label: "Calendario / reserva" },
  { value: "star", label: "Estrella" },
  { value: "verified", label: "Verificado" },
  { value: "autoawesome", label: "Destacado / brillo" },
  { value: "arrow", label: "Flecha" },
]);

const HIGHLIGHT_ICON_OPTIONS = Object.freeze([
  { value: "", label: "Sin ícono" },
  { value: "star", label: "Estrella" },
  { value: "support", label: "Atención" },
  { value: "verified", label: "Verificado" },
  { value: "speed", label: "Rapidez" },
  { value: "quality", label: "Calidad" },
  { value: "local", label: "Local" },
  { value: "security", label: "Seguridad" },
  { value: "autoawesome", label: "Elegancia / Destacado" },
  { value: "event", label: "Evento / Reserva" },
  { value: "groups", label: "Equipo / Personas" },
  { value: "photo", label: "Fotografía" },
  { value: "design", label: "Diseño / Creatividad" },
]);

const SEGMENT_PRESENTATION = Object.freeze({
  [SEGMENT_TYPES.NAVBAR]: Object.freeze({
    group: "structure",
    summary: "Identidad y accesos principales para orientarse rápido.",
    placement: "Primero, arriba",
    visualHint: "Barra horizontal con logo y enlaces",
    preview: "navigation",
  }),
  [SEGMENT_TYPES.HERO]: Object.freeze({
    group: "structure",
    summary: "Presenta el negocio con un mensaje fuerte y una acción clara.",
    placement: "Inicio de la página",
    visualHint: "Título grande, texto, imagen y botón",
    preview: "hero",
  }),
  [SEGMENT_TYPES.SPOTLIGHT]: Object.freeze({
    group: "commerce",
    summary: "Destaca una promoción, producto o servicio puntual.",
    placement: "Antes del catálogo",
    visualHint: "Bloque protagonista tipo promo",
    preview: "promo",
  }),
  [SEGMENT_TYPES.FEATURED]: Object.freeze({
    group: "commerce",
    summary: "Pone primero una selección corta de productos o servicios.",
    placement: "Zona comercial",
    visualHint: "Fila de tarjetas destacadas",
    preview: "cards",
  }),
  [SEGMENT_TYPES.BRANDS]: Object.freeze({
    group: "commerce",
    summary: "Refuerza variedad, respaldo o alianzas con marcas.",
    placement: "Zona comercial",
    visualHint: "Fila compacta de logos",
    preview: "logos",
  }),
  [SEGMENT_TYPES.CATEGORIES]: Object.freeze({
    group: "commerce",
    summary: "Da accesos rápidos a tipos de productos o servicios.",
    placement: "Antes o dentro del catálogo",
    visualHint: "Tarjetas cortas por categoría",
    preview: "categories",
  }),
  [SEGMENT_TYPES.ABOUT]: Object.freeze({
    group: "content",
    summary: "Cuenta quién sos, tu historia o la propuesta del negocio.",
    placement: "Después de la propuesta principal",
    visualHint: "Texto e imagen en dos columnas",
    preview: "split",
  }),
  [SEGMENT_TYPES.HIGHLIGHTS]: Object.freeze({
    group: "content",
    summary: "Resume beneficios o motivos concretos para elegirte.",
    placement: "Antes del contacto",
    visualHint: "Beneficios breves en tarjetas",
    preview: "benefits",
  }),
  [SEGMENT_TYPES.CONTACT]: Object.freeze({
    group: "content",
    summary: "Reúne datos y accesos para que puedan contactarte.",
    placement: "Cerca del final",
    visualHint: "Datos, acciones y ubicación",
    preview: "contact",
  }),
  [SEGMENT_TYPES.FOOTER]: Object.freeze({
    group: "closing",
    summary: "Cierra el sitio con identidad, enlaces y datos útiles.",
    placement: "Último, abajo",
    visualHint: "Franja final con varias columnas",
    preview: "footer",
  }),
  [SEGMENT_TYPES.FLOATING_WHATSAPP]: Object.freeze({
    group: "closing",
    summary: "Mantiene WhatsApp disponible durante todo el recorrido.",
    placement: "Flotante sobre la página",
    visualHint: "Botón circular fijo en una esquina",
    preview: "floating",
  }),
});

function clone(value) {
  if (value === undefined) return undefined;
  if (value === null || typeof value !== "object") return value;
  return JSON.parse(JSON.stringify(value));
}

function getAtPath(source, path, fallback) {
  const value = String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, rawKey) => {
      const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
      return current?.[key];
    }, source);
  return value === undefined ? fallback : value;
}

function field(path, type, options = {}) {
  const hasExplicitDefault = Object.prototype.hasOwnProperty.call(
    options,
    "defaultValue"
  );
  return {
    path,
    type,
    editable: options.editable !== false,
    defaultValue: clone(
      hasExplicitDefault ? options.defaultValue : getAtPath(emptySettings, path)
    ),
    ...options,
  };
}

function visualStyleProperty(sectionId) {
  return field("theme.template", SEGMENT_FIELD_TYPES.SEGMENT_VISUAL_STYLE, {
    key: "visualStyle",
    label: "Estilo visual del segmento",
    sectionId,
    defaultValue: clone(DEFAULT_SEGMENT_VISUAL_STYLE),
    limits: {
      titleSize: VISUAL_LEVEL_LIMITS,
      imageSize: VISUAL_LEVEL_LIMITS,
      spacing: VISUAL_LEVEL_LIMITS,
      textStyle: VISUAL_LEVEL_LIMITS,
    },
  });
}

function globalColorProperties() {
  return [
    field("theme.primaryColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color principal",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.secondaryColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color secundario",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.surfaceColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Superficie",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Fondo general",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.textColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Texto principal",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
    field("theme.mutedTextColor", SEGMENT_FIELD_TYPES.COLOR, {
      label: "Texto secundario",
      maxLength: 100,
      colorScope: "site",
      shared: true,
    }),
  ];
}

const SECTION_APPEARANCE_FALLBACKS = Object.freeze({
  about: Object.freeze({
    backgroundColor: { fallbackPath: "theme.backgroundColor" },
    surfaceColor: { fallbackValue: "transparent", overrideSeedPath: "theme.surfaceColor" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackPath: "theme.secondaryColor" },
    textColor: { fallbackPath: "theme.mutedTextColor" },
  }),
  highlights: Object.freeze({
    backgroundColor: { fallbackPath: "theme.surfaceColor" },
    surfaceColor: { fallbackPath: "theme.surfaceColor" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackPath: "theme.secondaryColor" },
    textColor: { fallbackPath: "theme.mutedTextColor" },
  }),
  contact: Object.freeze({
    backgroundColor: { fallbackPath: "theme.secondaryColor" },
    surfaceColor: { fallbackValue: "rgba(255,255,255,.08)", overrideSeedValue: "#FFFFFF" },
    accentColor: { fallbackPath: "theme.primaryColor" },
    titleColor: { fallbackValue: "#FFFFFF" },
    textColor: { fallbackValue: "rgba(255,255,255,.82)", overrideSeedValue: "#FFFFFF" },
  }),
});

function appearanceProperties(sectionId) {
  const root = `appearance.${sectionId}`;
  const fallbacks = SECTION_APPEARANCE_FALLBACKS[sectionId] || {};
  const optionsFor = (role) => ({
    maxLength: 100,
    colorScope: "section",
    ...(fallbacks[role] || {}),
  });

  return [
    field(`${root}.backgroundColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Fondo del segmento",
      ...optionsFor("backgroundColor"),
    }),
    field(`${root}.surfaceColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Superficie del segmento",
      ...optionsFor("surfaceColor"),
    }),
    field(`${root}.accentColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color destacado",
      ...optionsFor("accentColor"),
    }),
    field(`${root}.titleColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color de títulos",
      ...optionsFor("titleColor"),
    }),
    field(`${root}.textColor`, SEGMENT_FIELD_TYPES.COLOR, {
      label: "Color de textos",
      ...optionsFor("textColor"),
    }),
  ];
}

function commonSegment({ id, visibilityPath, requiresProducts = false, properties }) {
  return {
    id,
    type: id,
    label: SEGMENT_LABELS[id] || id,
    visibilityPath,
    requiresProducts,
    defaultVisible: visibilityPath
      ? getAtPath(emptySettings, visibilityPath, true) !== false
      : true,
    presentation: SEGMENT_PRESENTATION[id] || null,
    properties,
  };
}

const registry = {
  [SEGMENT_TYPES.NAVBAR]: commonSegment({
    id: SEGMENT_TYPES.NAVBAR,
    visibilityPath: "sections.showNavbar",
    properties: [
      field("sections.showNavbar", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar encabezado",
      }),
      field("navigation.style", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Estilo del encabezado",
        options: STYLE_OPTIONS,
      }),
      field("navigation.showLogo", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar logo en la barra",
      }),
      field("navigation.homeLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Inicio",
        maxLength: 80,
      }),
      field("navigation.aboutLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Sobre nosotros",
        maxLength: 100,
      }),
      field("navigation.highlightsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Por qué elegirnos",
        maxLength: 100,
      }),
      field("navigation.productsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Productos o servicios",
        maxLength: 80,
      }),
      field("navigation.allProductsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón del mega menú",
        maxLength: 100,
      }),
      field("navigation.megaMenuStyle", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Estilo del mega menú",
        options: MEGA_MENU_STYLE_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuContent", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Contenido del mega menú",
        options: MEGA_MENU_CONTENT_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuDisplay", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Qué mostrar",
        options: MEGA_MENU_DISPLAY_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.megaMenuHierarchy", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Orden de categorías",
        options: MEGA_MENU_HIERARCHY_OPTIONS,
        uiGroup: "behavior",
      }),
      field("navigation.showMegaMenuIntro", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar bloque destacado del mega menú",
      }),
      field("navigation.megaMenuEyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior del mega menú",
        maxLength: 120,
      }),
      field("navigation.megaMenuTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título del mega menú",
        maxLength: 180,
      }),
      field("navigation.megaMenuDescription", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción del mega menú",
        maxLength: 500,
      }),
      field("navigation.showMegaMenuStats", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar cantidades en el bloque destacado",
      }),
      field("navigation.megaMenuCategoriesSubtitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto debajo del título de categorías",
        maxLength: 220,
      }),
      field("navigation.megaMenuSortLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto auxiliar de categorías",
        maxLength: 120,
      }),
      field("navigation.showMegaMenuLatest", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar últimos agregados",
      }),
      field("navigation.megaMenuLatestLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título de últimos agregados",
        maxLength: 120,
      }),
      field("navigation.loadingCategoriesText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto mientras carga el mega menú",
        maxLength: 180,
      }),
      field("navigation.emptyCategoriesText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto si no hay categorías",
        maxLength: 220,
      }),
      field("navigation.categoriesErrorText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto si falla el mega menú",
        maxLength: 220,
      }),
      field("navigation.contactLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Contacto",
        maxLength: 80,
      }),
      field("navigation.loginLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Acceso de clientes",
        maxLength: 100,
      }),
      field("navigation.accountLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible de la cuenta",
        maxLength: 100,
      }),
      field("navigation.buttons", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Botones de la navbar",
        navbarButtons: true,
        limits: { maxItems: 8 },
        item: {
          fields: [
            { key: "id", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 100 },
            { key: "label", label: "Texto del botón", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 80 },
            {
              key: "behavior",
              label: "Comportamiento",
              type: SEGMENT_FIELD_TYPES.SELECT,
              options: [
                { value: "section", label: "Deslizar a una sección" },
                { value: "link", label: "Abrir un link" },
                { value: "mega-menu", label: "Desplegar mega menú" },
              ],
            },
            { key: "target", label: "Destino", type: SEGMENT_FIELD_TYPES.URL, maxLength: URL_MAX_LENGTH },
          ],
        },
      }),
      field("navigation.homeUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Inicio",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.aboutUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Sobre nosotros",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.highlightsUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Por qué elegirnos",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.productsUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Productos o servicios",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.contactUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino de Contacto",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("navigation.loginUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del acceso de clientes",
        maxLength: URL_MAX_LENGTH,
      }),
      field("navigation.showAboutLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Sobre nosotros",
      }),
      field("navigation.showHighlightsLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Por qué elegirnos",
      }),
      field("navigation.showProductsLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Productos",
      }),
      field("navigation.showContactLink", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar enlace Contacto",
      }),
      field("navigation.showCart", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar carrito",
      }),
      field("navigation.showCustomerAccess", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar acceso de clientes",
      }),
      field("navigation.showSocialLinks", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar redes sociales",
      }),
      field("navigation.menuAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible para abrir el menú",
        maxLength: 140,
      }),
      field("navigation.closeMenuAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible para cerrar el menú",
        maxLength: 140,
      }),
      field("navigation.cartAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible del carrito",
        maxLength: 140,
      }),
      field("business.name", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre del negocio",
        maxLength: 140,
        shared: true,
      }),
      field("theme.logoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo de la barra",
        maxLength: URL_MAX_LENGTH,
      }),
      field("theme.compactLogoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo de la barra en celular",
        maxLength: URL_MAX_LENGTH,
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.NAVBAR),
    ],
  }),

  [SEGMENT_TYPES.HERO]: commonSegment({
    id: SEGMENT_TYPES.HERO,
    visibilityPath: "sections.showHero",
    properties: [
      field("sections.showHero", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar portada",
      }),
      field("hero.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("hero.title", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Título principal",
        maxLength: 220,
      }),
      field("hero.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Texto de apoyo",
        maxLength: 900,
      }),
      field("hero.primaryCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón principal",
        maxLength: 100,
      }),
      field("hero.primaryCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón principal",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("hero.primaryCtaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón principal requiere catálogo",
      }),
      field("hero.primaryCtaShowIcon", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar ícono en el botón principal",
        uiGroup: "behavior",
      }),
      field("hero.primaryCtaIcon", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ícono del botón principal",
        options: HERO_CTA_ICON_OPTIONS,
        uiGroup: "behavior",
      }),
      field("hero.secondaryCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón secundario",
        maxLength: 100,
      }),
      field("hero.secondaryCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón secundario",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("hero.secondaryCtaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón secundario requiere catálogo",
      }),
      field("hero.secondaryCtaShowIcon", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar ícono en el botón secundario",
        uiGroup: "behavior",
      }),
      field("hero.secondaryCtaIcon", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ícono del botón secundario",
        options: HERO_CTA_ICON_OPTIONS,
        uiGroup: "behavior",
      }),
      field("hero.imageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen principal",
        maxLength: URL_MAX_LENGTH,
      }),
      field("hero.imagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("hero.contentHorizontalAlign", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición horizontal de los textos",
        options: [
          { value: "left", label: "Izquierda" },
          { value: "center", label: "Centro" },
          { value: "right", label: "Derecha" },
        ],
        uiGroup: "behavior",
      }),
      field("hero.contentVerticalAlign", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición vertical de los textos",
        options: [
          { value: "top", label: "Arriba" },
          { value: "center", label: "Centro" },
          { value: "bottom", label: "Abajo" },
        ],
        uiGroup: "behavior",
      }),
      field("hero.overlay", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Capa sobre la imagen",
        maxLength: 600,
      }),
      field("hero.rotatingPhrases", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Frases rotativas",
        limits: { maxItems: 12 },
        item: {
          type: SEGMENT_FIELD_TYPES.TEXT,
          maxLength: 180,
        },
      }),
      field("hero.slides", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Imágenes de portada",
        limits: { maxItems: 6 },
        item: {
          fields: [
            { key: "url", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH },
            { key: "alt", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "title", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "subtitle", type: SEGMENT_FIELD_TYPES.TEXTAREA, maxLength: 700 },
            { key: "position", label: "Posición de la imagen", type: SEGMENT_FIELD_TYPES.SELECT, options: IMAGE_POSITION_OPTIONS },
          ],
        },
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.HERO),
    ],
  }),

  [SEGMENT_TYPES.SPOTLIGHT]: commonSegment({
    id: SEGMENT_TYPES.SPOTLIGHT,
    visibilityPath: "sections.showSpotlight",
    requiresProducts: true,
    properties: [
      field("sections.showSpotlight", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Permitir mostrar el bloque",
      }),
      field("catalog.spotlight.enabled", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Activar bloque destacado",
      }),
      field("catalog.spotlight.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.spotlight.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.spotlight.description", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 1200,
      }),
      field("catalog.spotlight.logoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo o imagen principal",
        maxLength: URL_MAX_LENGTH,
      }),
      field("catalog.spotlight.logoAlt", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto alternativo del logo",
        maxLength: 220,
      }),
      field("catalog.spotlight.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de fondo",
        maxLength: URL_MAX_LENGTH,
      }),
      field("catalog.spotlight.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("catalog.spotlight.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.spotlight.ctaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("catalog.spotlight.ctaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón requiere catálogo",
      }),
      field("catalog.spotlight.primaryColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Color principal del destacado",
        maxLength: 100,
        colorScope: "section",
        fallbackPath: "theme.primaryColor",
      }),
      field("catalog.spotlight.secondaryColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Color secundario del destacado",
        maxLength: 100,
        colorScope: "section",
        fallbackPath: "theme.secondaryColor",
      }),
      visualStyleProperty(SEGMENT_TYPES.SPOTLIGHT),
    ],
  }),

  [SEGMENT_TYPES.FEATURED]: commonSegment({
    id: SEGMENT_TYPES.FEATURED,
    visibilityPath: "sections.showFeatured",
    requiresProducts: true,
    properties: [
      field("sections.showProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar catálogo",
      }),
      field("sections.showFeatured", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar destacados",
      }),
      field("catalog.featured.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.featured.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.featured.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.featured.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.featured.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin resultados",
        maxLength: 300,
      }),
      field("catalog.featured.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.featured.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.cards.gap", SEGMENT_FIELD_TYPES.NUMBER, { label: "Separación entre cards", limits: { min: 0, max: 64, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.radius", SEGMENT_FIELD_TYPES.NUMBER, { label: "Redondez de las cards", limits: { min: 0, max: 48, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de las cards", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.backgroundOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Transparencia del fondo", limits: { min: 0, max: 1, step: 0.05 }, uiGroup: "design" }),
      field("catalog.cards.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de fondo de las cards",
        maxLength: URL_MAX_LENGTH,
        positionPath: "catalog.cards.backgroundImagePosition",
      }),
      field("catalog.cards.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición del fondo de las cards",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("catalog.cards.backgroundImageFit", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ajuste del fondo de las cards",
        options: [
          { value: "cover", label: "Cubrir la card" },
          { value: "contain", label: "Mostrar completa" },
        ],
        uiGroup: "design",
      }),
      field("catalog.cards.backgroundImageOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Opacidad de la imagen de fondo", limits: { min: 0, max: 1, step: 0.05 }, uiGroup: "design" }),
      field("catalog.cards.imagePosition", SEGMENT_FIELD_TYPES.SELECT, { label: "Posición de la foto del ítem", options: [
        { value: "top", label: "Arriba" }, { value: "left", label: "Izquierda" }, { value: "right", label: "Derecha" },
      ], uiGroup: "design" }),
      field("catalog.cards.imageFit", SEGMENT_FIELD_TYPES.SELECT, { label: "Comportamiento de la foto del ítem", options: [
        { value: "cover", label: "Cubrir" }, { value: "contain", label: "Contener completa" },
      ], uiGroup: "design" }),
      field("catalog.cards.imageHeight", SEGMENT_FIELD_TYPES.NUMBER, { label: "Alto de imagen", limits: { min: 90, max: 420, step: 5 }, uiGroup: "design" }),
      field("catalog.cards.showImage", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar imagen del ítem" }),
      field("catalog.cards.showTitle", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar nombre / título" }),
      field("catalog.cards.showButton", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar botón principal" }),
      field("catalog.cards.showType", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar tipo de ítem" }),
      field("catalog.cards.showCategory", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar categoría" }),
      field("catalog.cards.showSubcategory", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar subcategoría" }),
      field("catalog.cards.showDescription", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar descripción" }),
      field("catalog.cards.showPrice", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar precio" }),
      field("catalog.cards.showDuration", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar duración en servicios" }),
      field("catalog.cards.showCapabilities", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar reserva / entrega" }),
      field("catalog.cards.showQuickCart", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar compra rápida" }),
      field("catalog.cards.borderColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Borde de la card", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.shadowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Sombra de la card", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.typeColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del tipo", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.titleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del título", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.categoryColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de categoría", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.subcategoryColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de subcategoría", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.descriptionColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de descripción", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.priceColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del precio", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Texto del botón", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo del botón", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonHoverTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Texto del botón al pasar el mouse", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonHoverBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo del botón al pasar el mouse", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, { label: "Botón para productos", maxLength: 100 }),
      field("catalog.cards.serviceCtaLabel", SEGMENT_FIELD_TYPES.TEXT, { label: "Botón para servicios", maxLength: 100 }),
      field("catalog.cards.ctaBehavior", SEGMENT_FIELD_TYPES.SELECT, { label: "Destino del botón", options: [
        { value: "detail", label: "Detalle / reserva del ítem" }, { value: "custom", label: "Link personalizado" },
      ], uiGroup: "behavior" }),
      field("catalog.cards.ctaUrl", SEGMENT_FIELD_TYPES.URL, { label: "Link personalizado de las cards", maxLength: URL_MAX_LENGTH }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.FEATURED),
    ],
  }),

  [SEGMENT_TYPES.BRANDS]: commonSegment({
    id: SEGMENT_TYPES.BRANDS,
    visibilityPath: "sections.showBrands",
    requiresProducts: true,
    properties: [
      field("sections.showBrands", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar marcas",
      }),
      field("catalog.brands.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.brands.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.brands.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.brands.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.brands.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin marcas",
        maxLength: 300,
      }),
      field("catalog.brands.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.brands.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.brands.items", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Marcas",
        limits: { maxItems: 40 },
        item: {
          fields: [
            { key: "name", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 160 },
            { key: "logoUrl", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH },
            { key: "alt", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "query", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 180 },
          ],
        },
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.BRANDS),
    ],
  }),

  [SEGMENT_TYPES.CATEGORIES]: commonSegment({
    id: SEGMENT_TYPES.CATEGORIES,
    visibilityPath: "sections.showCategories",
    requiresProducts: true,
    properties: [
      field("sections.showCategories", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar categorías",
      }),
      field("catalog.categories.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.categories.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.categories.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.categories.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.categories.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin categorías",
        maxLength: 300,
      }),
      field("catalog.categories.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.categories.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.categories.productSingular", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre singular del producto",
        maxLength: 60,
      }),
      field("catalog.categories.productPlural", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre plural del producto",
        maxLength: 60,
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.CATEGORIES),
    ],
  }),

  [SEGMENT_TYPES.ABOUT]: commonSegment({
    id: SEGMENT_TYPES.ABOUT,
    visibilityPath: "sections.showAbout",
    properties: [
      field("sections.showAbout", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar sección",
      }),
      field("about.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("about.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("about.description", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Historia o descripción",
        maxLength: 2500,
      }),
      field("about.imageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen del negocio",
        maxLength: URL_MAX_LENGTH,
      }),
      field("about.imageAlt", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto alternativo de la imagen",
        maxLength: 220,
      }),
      field("about.layout", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Distribución",
        options: [
          { value: "image-left", label: "Imagen a la izquierda" },
          { value: "image-right", label: "Imagen a la derecha" },
        ],
      }),
      field("about.imagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("about.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("about.ctaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("about.ctaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón requiere catálogo",
      }),
      ...appearanceProperties(SEGMENT_TYPES.ABOUT),
      visualStyleProperty(SEGMENT_TYPES.ABOUT),
    ],
  }),

  [SEGMENT_TYPES.HIGHLIGHTS]: commonSegment({
    id: SEGMENT_TYPES.HIGHLIGHTS,
    visibilityPath: "sections.showHighlights",
    properties: [
      field("sections.showHighlights", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar diferenciales",
      }),
      field("highlightsIntro.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("highlightsIntro.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("highlightsIntro.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("highlights", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Diferenciales",
        limits: { maxItems: 24 },
        item: {
          fields: [
            { key: "id", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 100 },
            { key: "icon", type: SEGMENT_FIELD_TYPES.SELECT, maxLength: 50, options: HIGHLIGHT_ICON_OPTIONS },
            { key: "title", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 160 },
            { key: "description", type: SEGMENT_FIELD_TYPES.TEXTAREA, maxLength: 600 },
            { key: "imageUrl", label: "Imagen de la tarjeta", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH },
            { key: "imageAlt", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "imagePosition", label: "Posición de la imagen", type: SEGMENT_FIELD_TYPES.SELECT, options: IMAGE_POSITION_OPTIONS },
            { key: "youtubeUrl", label: "Video de YouTube", type: SEGMENT_FIELD_TYPES.URL, maxLength: URL_MAX_LENGTH },
          ],
        },
      }),
      field("appearance.highlights.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Foto de fondo opcional",
        maxLength: URL_MAX_LENGTH,
        positionPath: "appearance.highlights.backgroundImagePosition",
      }),
      field("appearance.highlights.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la foto de fondo",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("appearance.highlights.backgroundImageOpacity", SEGMENT_FIELD_TYPES.NUMBER, {
        label: "Intensidad de la foto de fondo",
        min: 0,
        max: 1,
        step: 0.05,
        defaultValue: 0.24,
      }),
      ...appearanceProperties(SEGMENT_TYPES.HIGHLIGHTS),
      visualStyleProperty(SEGMENT_TYPES.HIGHLIGHTS),
    ],
  }),

  [SEGMENT_TYPES.CONTACT]: commonSegment({
    id: SEGMENT_TYPES.CONTACT,
    visibilityPath: "sections.showContact",
    properties: [
      field("sections.showContact", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar contacto",
      }),
      field("contact.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("contact.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Texto de apoyo",
        maxLength: 900,
      }),
      field("contact.whatsappMessage", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje inicial de WhatsApp",
        maxLength: 500,
      }),
      field("contact.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de fondo",
        maxLength: URL_MAX_LENGTH,
      }),
      field("contact.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("contact.detailsCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón de detalles",
        maxLength: 100,
      }),
      field("contact.detailsCtaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón de detalles",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("contact.whatsappCtaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón de WhatsApp",
        maxLength: 100,
      }),
      field("contact.mapTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título del mapa",
        maxLength: 180,
      }),
      field("business.whatsapp", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Número de WhatsApp",
        maxLength: 40,
        shared: true,
      }),
      field("business.phone", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Teléfono",
        maxLength: 80,
        shared: true,
      }),
      field("business.email", SEGMENT_FIELD_TYPES.EMAIL, {
        label: "Correo electrónico",
        maxLength: 180,
        shared: true,
      }),
      field("business.address", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Dirección",
        maxLength: 260,
        shared: true,
      }),
      field("business.city", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Ciudad",
        maxLength: 120,
        shared: true,
      }),
      field("business.province", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Provincia",
        maxLength: 120,
        shared: true,
      }),
      field("business.country", SEGMENT_FIELD_TYPES.TEXT, {
        label: "País",
        maxLength: 120,
        shared: true,
      }),
      field("business.schedule", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Horarios",
        maxLength: 400,
        shared: true,
      }),
      field("business.mapEmbedUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Mapa embebido",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.instagram", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Instagram",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.facebook", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Facebook",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.linkedin", SEGMENT_FIELD_TYPES.TEXT, {
        label: "LinkedIn",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      field("business.socials.website", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Sitio externo",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      ...appearanceProperties(SEGMENT_TYPES.CONTACT),
      visualStyleProperty(SEGMENT_TYPES.CONTACT),
    ],
  }),

  [SEGMENT_TYPES.FOOTER]: commonSegment({
    id: SEGMENT_TYPES.FOOTER,
    visibilityPath: "sections.showFooter",
    properties: [
      field("sections.showFooter", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar pie de página",
      }),
      field("footer.style", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Estilo del pie de página",
        options: STYLE_OPTIONS,
      }),
      field("footer.showLogo", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar logo",
      }),
      field("footer.logoPosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición del logo",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("footer.tagline", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción corta",
        maxLength: 700,
      }),
      field("footer.copyright", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Derechos de autor",
        maxLength: 220,
      }),
      field("footer.navigationTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título de navegación",
        maxLength: 120,
      }),
      field("footer.contactTitle", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título de contacto",
        maxLength: 120,
      }),
      field("footer.homeLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Inicio",
        maxLength: 80,
      }),
      field("footer.productsLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Productos o servicios",
        maxLength: 80,
      }),
      field("footer.contactLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Contacto",
        maxLength: 80,
      }),
      field("business.name", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre del negocio",
        maxLength: 140,
        shared: true,
      }),
      field("business.shortDescription", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción breve del negocio",
        maxLength: 700,
        shared: true,
      }),
      field("theme.logoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo principal",
        maxLength: URL_MAX_LENGTH,
        shared: true,
        positionPath: "footer.logoPosition",
      }),
      field("theme.compactLogoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo compacto",
        maxLength: URL_MAX_LENGTH,
        shared: true,
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.FOOTER),
    ],
  }),

  [SEGMENT_TYPES.FLOATING_WHATSAPP]: commonSegment({
    id: SEGMENT_TYPES.FLOATING_WHATSAPP,
    visibilityPath: "sections.showFloatingWhatsapp",
    properties: [
      field("sections.showFloatingWhatsapp", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar WhatsApp flotante",
      }),
      field("business.whatsapp", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Número de WhatsApp",
        maxLength: 40,
        shared: true,
      }),
      field("contact.whatsappMessage", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje inicial",
        maxLength: 500,
        shared: true,
      }),
      field("contact.floatingWhatsappAriaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Descripción accesible del botón",
        maxLength: 160,
      }),
      field("theme.primaryColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Color principal del sitio",
        maxLength: 100,
        shared: true,
        colorScope: "site",
      }),
    ],
  }),
};

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

export const SEGMENT_REGISTRY = deepFreeze(registry);
export const SEGMENT_ORDER = Object.freeze([
  SEGMENT_TYPES.NAVBAR,
  SEGMENT_TYPES.HERO,
  SEGMENT_TYPES.SPOTLIGHT,
  SEGMENT_TYPES.FEATURED,
  SEGMENT_TYPES.BRANDS,
  SEGMENT_TYPES.CATEGORIES,
  SEGMENT_TYPES.ABOUT,
  SEGMENT_TYPES.HIGHLIGHTS,
  SEGMENT_TYPES.CONTACT,
  SEGMENT_TYPES.FOOTER,
  SEGMENT_TYPES.FLOATING_WHATSAPP,
]);

export function getSegmentDefinition(segmentType) {
  return SEGMENT_REGISTRY[segmentType] || null;
}


export function buildSegmentVisibilityChanges(segmentType, nextVisible) {
  const definition = getSegmentDefinition(segmentType);
  const visibilityPath = definition?.visibilityPath;
  if (!visibilityPath) return {};

  const visible = nextVisible === true;
  const changes = { [visibilityPath]: visible };

  (definition.properties || [])
    .filter(
      (property) =>
        property.type === SEGMENT_FIELD_TYPES.BOOLEAN &&
        /\.enabled$/.test(property.path || "")
    )
    .forEach((property) => {
      changes[property.path] = visible;
    });

  if (definition.requiresProducts && visible) {
    changes["sections.showProducts"] = true;
  }

  return changes;
}

export function getSegmentEditableProperties(segmentType) {
  return getSegmentDefinition(segmentType)?.properties || [];
}

export function getSegmentDefaultValues(segmentType) {
  const definition = getSegmentDefinition(segmentType);
  if (!definition) return {};
  return definition.properties.reduce((defaults, property) => {
    if (property.key === "visualStyle") {
      defaults.visualStyle = clone(property.defaultValue);
      return defaults;
    }
    defaults[property.path] = clone(property.defaultValue);
    return defaults;
  }, {});
}

export function isKnownSegmentType(segmentType) {
  return Object.prototype.hasOwnProperty.call(SEGMENT_REGISTRY, segmentType);
}

export default SEGMENT_REGISTRY;
